import { useTranslation } from "react-i18next";
import { Badge as PolarisBadge } from "@shopify/polaris";
import { IncidentAction } from "services/work-security-incidents.service";

type Props = {
  actions: IncidentAction[];
  /** True while another request runs or the incident no longer accepts changes. */
  disabled: boolean;
  formatDate: (ms: number) => string;
  onToggleDone: (action: IncidentAction) => void;
  onDelete: (action: IncidentAction) => void;
};

const buttonCls =
  "inline-flex min-h-11 w-full items-center justify-center rounded-lg border bg-white px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50";

/** Corrective actions of an incident as one card each: the table's five columns do not fit a phone. */
export default function CorrectiveActionCards({ actions, disabled, formatDate, onToggleDone, onDelete }: Props) {
  const { t } = useTranslation();

  return (
    <ul className="space-y-3">
      {actions.map((action) => (
        <li key={action.id} className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-800">
          <div className="flex items-start justify-between gap-2">
            <p className="break-words text-sm font-medium text-zinc-900 dark:text-white">{action.description}</p>
            <div className="shrink-0">
              {action.doneAt ? (
                <PolarisBadge tone="success">{t("done", { defaultValue: "Done" })}</PolarisBadge>
              ) : (
                <PolarisBadge tone="attention">{t("open", { defaultValue: "Open" })}</PolarisBadge>
              )}
            </div>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            {[
              action.ownerName,
              action.dueDate ? `${t("incident_action_due", { defaultValue: "Due" })}: ${formatDate(action.dueDate)}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <div className="mt-3 space-y-3">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onToggleDone(action)}
              className={`${buttonCls} border-zinc-300 text-zinc-700 hover:bg-zinc-50`}
            >
              {action.doneAt
                ? t("mark_open", { defaultValue: "Mark open" })
                : t("mark_done", { defaultValue: "Mark done" })}
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onDelete(action)}
              className={`${buttonCls} border-red-300 text-red-700 hover:bg-red-50`}
            >
              {t("delete", { defaultValue: "Delete" })}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
